import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams } from 'react-router-dom';
import { useAuthentication, useNotifications } from '../../store/AppContext';
import { DESIGN_TOKENS } from '../../styles/theme';
import { ErrorDisplay, LoadingSpinner, LocationPickerMap } from '../../components/common';
import { useUserProfile } from '../../hooks';
import { geocode, reverseGeocode } from '../../utils/geocoding';
import { hasNonEmptyValue, hasValidCoordinatePair, isValidEmail, isValidIban, isValidNip, isValidPolishPhoneNumber, isValidRegon, isValidWebsiteUrl, resolveAssetUrl } from '../../utils';
import { updatePilotProfile, updateTransportCompany } from '../../api/services';
import {
  buildProfileUpdatePayload,
  createProfileFormState,
  isCompanyProfileFormState,
  isPilotProfileFormState,
} from './profileForm';
import type { UserProfileData } from '../../hooks';
import type {
  BrandGraphicsFormData,
  CompanyProfileFormState,
  MarketingConsents,
  PilotProfileFormState,
  ProfileFormState,
} from './profileForm';
import type { Permission, StrapiFile } from '../../types';

interface EditProfileSectionProps {
  embedded?: boolean;
}

function validateBusinessProfileFields(formData: {
  company_name?: string;
  name?: string;
  surname?: string;
  nip: string;
  regon: string;
  email: string;
  phone: string;
  website?: string;
  lat: string;
  lng: string;
  permissions?: Permission[];
  ibanAccount?: string;
}, options?: { requireCompanyName?: boolean; requirePersonalName?: boolean; requirePermissions?: boolean; validateWebsite?: boolean; validateIban?: boolean }) {
  if (options?.requireCompanyName && !hasNonEmptyValue(formData.company_name)) {
    return 'Podaj nazwę firmy.';
  }

  if (options?.requirePersonalName) {
    if (!hasNonEmptyValue(formData.name) || !hasNonEmptyValue(formData.surname)) {
      return 'Uzupełnij imię i nazwisko.';
    }
  }

  if (options?.requirePermissions && (!formData.permissions || formData.permissions.length === 0)) {
    return 'Wybierz co najmniej jedno uprawnienie.';
  }

  if (!isValidNip(formData.nip.trim())) {
    return 'Podaj poprawny numer NIP.';
  }

  if (!hasNonEmptyValue(formData.regon) || !isValidRegon(formData.regon.trim())) {
    return 'Podaj poprawny numer REGON.';
  }

  if (!isValidEmail(formData.email.trim())) {
    return 'Podaj poprawny adres e-mail.';
  }

  if (hasNonEmptyValue(formData.phone) && !isValidPolishPhoneNumber(formData.phone.trim())) {
    return 'Podaj poprawny numer telefonu albo zostaw pole puste.';
  }

  if (options?.validateWebsite && !isValidWebsiteUrl((formData.website ?? '').trim())) {
    return 'Podaj poprawny adres strony internetowej.';
  }

  if (!hasValidCoordinatePair(formData.lat, formData.lng)) {
    return 'Podaj poprawne współrzędne albo pozostaw oba pola puste.';
  }

  if (options?.validateIban && !isValidIban(formData.ibanAccount ?? '')) {
    return 'Podaj poprawny numer IBAN albo zostaw pole puste.';
  }

  return null;
}

function filePreviewUrl(file?: File | null, fallbackUrl?: string): string | undefined {
  if (file) return URL.createObjectURL(file);
  return fallbackUrl;
}

type ProfileFormStateSetter = React.Dispatch<React.SetStateAction<ProfileFormState | null>>;

function RemoveMediaButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        size="small"
        onClick={onClick}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          backgroundColor: 'rgba(13, 27, 42, 0.72)',
          color: '#fff',
          '&:hover': {
            backgroundColor: DESIGN_TOKENS.colors.error,
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
}

function BrandGraphicsFields({
  formData,
  setFormData,
}: {
  formData: BrandGraphicsFormData;
  setFormData: ProfileFormStateSetter;
}) {
  const logoUrl = filePreviewUrl(formData.logoFile, resolveAssetUrl(formData.logo?.url));
  const coverUrl = filePreviewUrl(formData.coverImageFile, resolveAssetUrl(formData.coverImage?.url));
  const galleryFiles = formData.galleryFiles ?? [];
  const hasGallery = Boolean(formData.gallery?.length || galleryFiles.length);

  React.useEffect(() => {
    return () => {
      if (logoUrl?.startsWith('blob:')) URL.revokeObjectURL(logoUrl);
      if (coverUrl?.startsWith('blob:')) URL.revokeObjectURL(coverUrl);
    };
  }, [coverUrl, logoUrl]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Grafika firmy</Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj logotypem, zdjęciem w tle i galerią widoczną w katalogu oraz profilu.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                backgroundColor: DESIGN_TOKENS.colors.background,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Logotyp</Typography>
              <Box sx={{ flexGrow: 1, minHeight: 220, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: 132,
                    height: 132,
                    borderRadius: 2,
                    border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                    position: 'relative',
                  }}
                >
                  {logoUrl ? (
                    <>
                      <Box component="img" src={logoUrl} alt="Logotyp" sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }} />
                      <RemoveMediaButton
                        label="Usuń logo"
                        onClick={() => setFormData((prev) => (prev ? { ...prev, logo: undefined, logoFile: null } : prev))}
                      />
                    </>
                  ) : (
                    <BusinessIcon color="disabled" sx={{ fontSize: 42 }} />
                  )}
                </Box>
              </Box>
              <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                Wybierz logo
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => setFormData((prev) => (prev ? { ...prev, logoFile: event.target.files?.[0] ?? null } : prev))}
                />
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                backgroundColor: DESIGN_TOKENS.colors.background,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Zdjęcie w tle</Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 220,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                  overflow: 'hidden',
                  backgroundColor: DESIGN_TOKENS.colors.surface,
                  position: 'relative',
                }}
              >
                {coverUrl ? (
                  <>
                    <Box component="img" src={coverUrl} alt="Zdjęcie w tle" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <RemoveMediaButton
                      label="Usuń zdjęcie w tle"
                      onClick={() => setFormData((prev) => (prev ? { ...prev, coverImage: undefined, coverImageFile: null } : prev))}
                    />
                  </>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CloudUploadIcon color="disabled" sx={{ fontSize: 44 }} />
                  </Box>
                )}
              </Box>
              <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                Wybierz zdjęcie
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => setFormData((prev) => (prev ? { ...prev, coverImageFile: event.target.files?.[0] ?? null } : prev))}
                />
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                backgroundColor: DESIGN_TOKENS.colors.background,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Galeria</Typography>
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
                  Dodaj zdjęcia
                  <input
                    hidden
                    multiple
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const files = Array.from(event.target.files ?? []);
                      setFormData((prev) => (prev ? { ...prev, galleryFiles: [...(prev.galleryFiles ?? []), ...files] } : prev));
                    }}
                  />
                </Button>
              </Box>

              {hasGallery ? (
                <Grid container spacing={1.5}>
                  {(formData.gallery ?? []).map((image) => (
                    <Grid item xs={6} sm={4} md={2} key={image.id}>
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={resolveAssetUrl(image.url)}
                          alt={image.name}
                          sx={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            display: 'block',
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                            backgroundColor: DESIGN_TOKENS.colors.surface,
                          }}
                        />
                        <RemoveMediaButton
                          label="Usuń zdjęcie z galerii"
                          onClick={() => setFormData((prev) => (prev ? {
                            ...prev,
                            gallery: (prev.gallery ?? []).filter((galleryImage: StrapiFile) => galleryImage.id !== image.id),
                          } : prev))}
                        />
                      </Box>
                    </Grid>
                  ))}
                  {galleryFiles.map((file, index) => {
                    const previewUrl = URL.createObjectURL(file);
                    return (
                      <Grid item xs={6} sm={4} md={2} key={`${file.name}-${file.lastModified}-${index}`}>
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            component="img"
                            src={previewUrl}
                            alt={file.name}
                            onLoad={() => URL.revokeObjectURL(previewUrl)}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              display: 'block',
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                              backgroundColor: DESIGN_TOKENS.colors.surface,
                            }}
                          />
                          <RemoveMediaButton
                            label="Usuń nowe zdjęcie"
                            onClick={() => setFormData((prev) => (prev ? {
                              ...prev,
                              galleryFiles: (prev.galleryFiles ?? []).filter((_: File, fileIndex: number) => fileIndex !== index),
                            } : prev))}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box
                  sx={{
                    minHeight: 112,
                    borderRadius: 2,
                    border: `1px dashed ${DESIGN_TOKENS.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                  }}
                >
                  <Typography variant="body2">Brak zdjęć w galerii.</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function ProfileSectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function ProfileLocationFields({
  address,
  city,
  email,
  lat,
  lng,
  onChange,
  onLocationSelect,
  phone,
  website,
}: {
  address: string;
  city: string;
  email: string;
  lat: string;
  lng: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLocationSelect: (lat: number, lng: number) => void;
  phone: string;
  website?: string;
}) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Email kontaktowy" name="email" required value={email} onChange={onChange} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Numer telefonu" name="phone" value={phone} onChange={onChange} />
      </Grid>
      {typeof website === 'string' && (
        <Grid item xs={12}>
          <TextField fullWidth label="Strona internetowa" name="website" value={website} onChange={onChange} />
        </Grid>
      )}
      <Grid item xs={12}>
        <LocationPickerMap lat={parseFloat(lat) || 0} lng={parseFloat(lng) || 0} onLocationSelect={onLocationSelect} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Miasto" name="city" value={city} onChange={onChange} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Adres" name="address" value={address} onChange={onChange} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Szerokość (Lat)" name="lat" value={lat} onChange={onChange} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Długość (Lng)" name="lng" value={lng} onChange={onChange} />
      </Grid>
    </Grid>
  );
}

function CompanyProfileFields({
  formData,
  onChange,
  onLocationSelect,
  setFormData,
}: {
  formData: CompanyProfileFormState;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLocationSelect: (lat: number, lng: number) => void;
  setFormData: ProfileFormStateSetter;
}) {
  return (
    <>
      <ProfileSectionCard title="Informacje o firmie">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth label="Nazwa firmy" name="company_name" required value={formData.company_name} onChange={onChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Opis działalności" name="description" multiline rows={4} value={formData.description} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="NIP" name="nip" required value={formData.nip} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="REGON *" name="regon" required value={formData.regon} onChange={onChange} />
          </Grid>
        </Grid>
      </ProfileSectionCard>

      <BrandGraphicsFields formData={formData} setFormData={setFormData} />

      <ProfileSectionCard title="Dane kontaktowe i adresowe">
        <ProfileLocationFields
          address={formData.address}
          city={formData.city}
          email={formData.email}
          lat={formData.lat}
          lng={formData.lng}
          onChange={onChange}
          onLocationSelect={onLocationSelect}
          phone={formData.phone}
          website={formData.website}
        />
      </ProfileSectionCard>
    </>
  );
}

function PilotProfileFields({
  formData,
  onChange,
  onLocationSelect,
  onPermissionChange,
  setFormData,
}: {
  formData: PilotProfileFormState;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLocationSelect: (lat: number, lng: number) => void;
  onPermissionChange: (permission: Permission) => void;
  setFormData: ProfileFormStateSetter;
}) {
  return (
    <>
      <ProfileSectionCard title="Dane osobowe">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Imię" name="name" required value={formData.name} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nazwisko" name="surname" required value={formData.surname} onChange={onChange} />
          </Grid>
        </Grid>
      </ProfileSectionCard>

      <ProfileSectionCard title="Posiadane uprawnienia">
        <FormGroup row>
          {(['BF2', 'BF3', 'BF4', 'HIPO'] as Permission[]).map((permission) => (
            <FormControlLabel
              key={permission}
              control={<Checkbox checked={formData.permissions.includes(permission)} onChange={() => onPermissionChange(permission)} />}
              label={permission}
            />
          ))}
        </FormGroup>
      </ProfileSectionCard>

      <BrandGraphicsFields formData={formData} setFormData={setFormData} />

      <ProfileSectionCard title="Dane firmy (B2B)">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="NIP" name="nip" required value={formData.nip} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="REGON" name="regon" value={formData.regon} onChange={onChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Numer konta IBAN" name="ibanAccount" value={formData.ibanAccount} onChange={onChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <ProfileLocationFields
            address={formData.address}
            city={formData.city}
            email={formData.email}
            lat={formData.lat}
            lng={formData.lng}
            onChange={onChange}
            onLocationSelect={onLocationSelect}
            phone={formData.phone}
          />
        </Box>
      </ProfileSectionCard>
    </>
  );
}

function useEditableProfileForm(profile: UserProfileData | null) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ProfileFormState | null>(null);
  const isInternalUpdate = React.useRef(false);

  React.useEffect(() => {
    if (!formData) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      if (formData.city) {
        const result = await geocode(formData.city);
        if (result) {
          isInternalUpdate.current = true;
          setFormData((prev) => prev ? ({
            ...prev,
            lat: result.lat.toString(),
            lng: result.lng.toString(),
          }) : prev);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData?.city]);

  React.useEffect(() => {
    if (!formData) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      const lat = parseFloat(formData.lat);
      const lng = parseFloat(formData.lng);

      if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0) {
        const result = await reverseGeocode(lat, lng);
        if (result && result.city) {
          isInternalUpdate.current = true;
          setFormData((prev) => prev ? ({
            ...prev,
            city: result.city || prev.city,
          }) : prev);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData?.lat, formData?.lng]);

  const handleLocationSelect = async (lat: number, lng: number) => {
    isInternalUpdate.current = true;
    setFormData((prev) => prev ? ({
      ...prev,
      lat: lat.toString(),
      lng: lng.toString(),
    }) : prev);

    const result = await reverseGeocode(lat, lng);
    if (result && result.city) {
      isInternalUpdate.current = true;
      setFormData((prev) => prev ? ({
        ...prev,
        city: result.city || '',
      }) : prev);
    }
  };

  React.useEffect(() => {
    if (profile?.fullData) {
      setFormData(createProfileFormState(profile));
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => prev ? { ...prev, [e.target.name]: e.target.value } : prev);
  };

  const handlePermissionChange = (permission: Permission) => {
    if (!formData) return;
    if (!('permissions' in formData)) return;
    const current = [...formData.permissions];
    if (current.includes(permission)) {
      setFormData({ ...formData, permissions: current.filter((p) => p !== permission) });
    } else {
      setFormData({ ...formData, permissions: [...current, permission] });
    }
  };

  return {
    formData,
    handleChange,
    handleLocationSelect,
    handlePermissionChange,
    isSubmitting,
    setFormData,
    setIsSubmitting,
  };
}

export function EditProfileSection({ embedded = false }: EditProfileSectionProps) {
  const { refreshUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile, isLoading, refetch } = useUserProfile();
  const {
    formData,
    handleChange,
    handleLocationSelect,
    handlePermissionChange,
    isSubmitting,
    setFormData,
    setIsSubmitting,
  } = useEditableProfileForm(profile);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !formData) return;

    const validationError = profile.type === 'COMPANY'
      ? validateBusinessProfileFields(formData, {
          requireCompanyName: true,
          validateWebsite: true,
        })
      : validateBusinessProfileFields(formData, {
          requirePersonalName: true,
          requirePermissions: true,
          validateIban: true,
        });

    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = await buildProfileUpdatePayload(profile, formData);
      const updateProfile = profile.type === 'COMPANY' ? updateTransportCompany : updatePilotProfile;
      await updateProfile(profile.documentId, payload);

      showNotification('Profil został zaktualizowany.', 'success');
      await refreshUser();
      refetch();
    } catch (err: any) {
      showNotification(err.message || 'Wystąpił błąd podczas aktualizacji profilu.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    const loadingContent = <LoadingSpinner message="Ładowanie danych profilu..." />;
    return embedded ? loadingContent : loadingContent;
  }

  if (!profile || !formData) {
    const errorContent = <ErrorDisplay message="Nie znaleziono profilu firmy lub pilota dla Twojego konta." onRetry={refetch} />;
    return embedded ? errorContent : errorContent;
  }

  const companyFormData =
    profile.type === 'COMPANY' && isCompanyProfileFormState(formData)
      ? formData
      : null;
  const pilotFormData =
    profile.type === 'PILOT' && isPilotProfileFormState(formData)
      ? formData
      : null;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {profile.type === 'COMPANY' && companyFormData ? (
        <CompanyProfileFields
          formData={companyFormData}
          onChange={handleChange}
          onLocationSelect={handleLocationSelect}
          setFormData={setFormData}
        />
      ) : null}

      {profile.type === 'PILOT' && pilotFormData ? (
        <PilotProfileFields
          formData={pilotFormData}
          onChange={handleChange}
          onLocationSelect={handleLocationSelect}
          onPermissionChange={handlePermissionChange}
          setFormData={setFormData}
        />
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Trwa zapisywanie...' : 'Zapisz zmiany'}
        </Button>
      </Box>
    </Box>
  );
}

export function AccountSettingsSection() {
  const { currentUser, refreshUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile, isLoading, refetch } = useUserProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTransportCompany = currentUser?.membership?.USER_TYPE === 'TRANSPORT_COMPANY';

  const [formData, setFormData] = useState({
    username: currentUser?.username ?? '',
    email: currentUser?.email ?? '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [marketingConsents, setMarketingConsents] = useState<MarketingConsents>({
    marketing_email_consent: false,
    marketing_sms_consent: false,
  });
  const [saveStatus, setSaveStatus] = useState<{ section: string; success: boolean } | null>(null);

  React.useEffect(() => {
    if (!profile?.fullData) return;

    setMarketingConsents({
      marketing_email_consent: Boolean(profile.fullData.marketing_email_consent),
      marketing_sms_consent: Boolean(profile.fullData.marketing_sms_consent),
    });
  }, [profile]);

  const currentTab = searchParams.get('tab') ?? 'account';
  const allowedTabs = isTransportCompany
    ? ['account', 'profile', 'marketing', 'tracking']
    : ['account', 'profile', 'marketing'];
  const normalizedTab = allowedTabs.includes(currentTab) ? currentTab : 'account';
  const activeTab = allowedTabs.indexOf(normalizedTab);

  const handleFieldChange =
    (fieldName: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [fieldName]: event.target.value }));
    };

  const handleSave = (section: string) => (event: React.FormEvent) => {
    event.preventDefault();
    if (section === 'profile' && !hasNonEmptyValue(formData.username)) {
      showNotification('Nazwa użytkownika nie może być pusta.', 'error');
      return;
    }

    if (section === 'email' && !isValidEmail(formData.email.trim())) {
      showNotification('Podaj poprawny adres e-mail.', 'error');
      return;
    }

    if (section === 'password') {
      if (
        !hasNonEmptyValue(formData.currentPassword) ||
        !hasNonEmptyValue(formData.newPassword) ||
        !hasNonEmptyValue(formData.confirmNewPassword)
      ) {
        showNotification('Wypełnij wszystkie pola hasła.', 'error');
        return;
      }

      if (formData.newPassword !== formData.confirmNewPassword) {
        showNotification('Nowe hasła nie są identyczne.', 'error');
        return;
      }

      if (formData.newPassword.trim().length < 8) {
        showNotification('Nowe hasło musi mieć co najmniej 8 znaków.', 'error');
        return;
      }
    }

    setSaveStatus({ section, success: true });
    setTimeout(() => setSaveStatus(null), 4000);
  };

  const handleMarketingConsentChange =
    (fieldName: keyof MarketingConsents) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setMarketingConsents((prev) => ({ ...prev, [fieldName]: event.target.checked }));
    };

  const handleMarketingSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!profile) return;

    try {
      if (profile.type === 'COMPANY') {
        await updateTransportCompany(profile.documentId, marketingConsents);
      } else {
        await updatePilotProfile(profile.documentId, marketingConsents);
      }

      setSaveStatus({ section: 'marketing', success: true });
      showNotification('Zgody marketingowe zostały zaktualizowane.', 'success');
      await refreshUser();
      refetch();
      setTimeout(() => setSaveStatus(null), 4000);
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się zaktualizować zgód marketingowych.', 'error');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Ładowanie ustawień konta..." />;
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            const tabValue = allowedTabs[value] ?? 'account';
            setSearchParams({ tab: tabValue });
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Ustawienia konta" />
          <Tab label="Mój profil" />
          <Tab label="Zgody marketingowe" />
          {isTransportCompany ? <Tab label="Śledzenie transportu" /> : null}
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Dane profilowe
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {saveStatus?.section === 'profile' && (
                  <Alert severity="success" sx={{ mb: 2.5 }}>
                    Nazwa użytkownika została zaktualizowana.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSave('profile')} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nazwa użytkownika"
                        value={formData.username}
                        onChange={handleFieldChange('username')}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button type="submit" variant="contained" color="primary">
                      Zapisz zmiany
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Zmiana adresu e-mail
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {saveStatus?.section === 'email' && (
                  <Alert severity="success" sx={{ mb: 2.5 }}>
                    Adres e-mail został zaktualizowany.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSave('email')} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Adres e-mail"
                        type="email"
                        value={formData.email}
                        onChange={handleFieldChange('email')}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button type="submit" variant="contained" color="primary">
                      Zapisz zmiany
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Zmiana hasła
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {saveStatus?.section === 'password' && (
                  <Alert severity="success" sx={{ mb: 2.5 }}>
                    Hasło zostało zmienione.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSave('password')} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        sx={{ maxWidth: 400 }}
                        fullWidth
                        label="Aktualne hasło"
                        type="password"
                        value={formData.currentPassword}
                        onChange={handleFieldChange('currentPassword')}
                        size="small"
                        autoComplete="current-password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nowe hasło"
                        type="password"
                        value={formData.newPassword}
                        onChange={handleFieldChange('newPassword')}
                        size="small"
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Potwierdź nowe hasło"
                        type="password"
                        value={formData.confirmNewPassword}
                        onChange={handleFieldChange('confirmNewPassword')}
                        size="small"
                        autoComplete="new-password"
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button type="submit" variant="contained" color="primary">
                      Zapisz zmiany
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card
              sx={{
                border: `1px solid ${DESIGN_TOKENS.colors.error}30`,
                boxShadow: 'none',
                mt: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="error.main" gutterBottom>
                  Strefa niebezpieczna
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Usunięcie konta jest nieodwracalne. Wszystkie Twoje dane zostaną trwale usunięte.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      if (window.confirm('Czy na pewno chcesz usunąć swoje konto? Tej operacji nie można cofnąć.')) {
                        // Call delete account API
                      }
                    }}
                  >
                    Usuń konto
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : activeTab === 1 ? (
        <EditProfileSection embedded />
      ) : activeTab === 2 ? (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Zgody marketingowe
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {saveStatus?.section === 'marketing' && (
              <Alert severity="success" sx={{ mb: 2.5 }}>
                Zgody marketingowe zostały zapisane.
              </Alert>
            )}

            <Box component="form" onSubmit={handleMarketingSave} noValidate>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingConsents.marketing_email_consent}
                      onChange={handleMarketingConsentChange('marketing_email_consent')}
                    />
                  }
                  label="Wyrażam zgodę na komunikację marketingową e-mail."
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingConsents.marketing_sms_consent}
                      onChange={handleMarketingConsentChange('marketing_sms_consent')}
                    />
                  }
                  label="Wyrażam zgodę na komunikację marketingową SMS."
                />
              </FormGroup>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Możesz w dowolnym momencie zmienić swoje zgody marketingowe w tym miejscu.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button type="submit" variant="contained" color="primary">
                  Zapisz zgody
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Śledzenie transportu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Podgląd śledzenia transportu jest osadzony bezpośrednio w panelu firmy transportowej.
            </Typography>
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                backgroundColor: DESIGN_TOKENS.colors.surface,
              }}
            >
              <Box
                component="iframe"
                src="https://qpro-82d9e.lm.r.appspot.com/"
                title="Śledzenie transportu"
                loading="lazy"
                scrolling="yes"
                sx={{
                  display: 'block',
                  width: '100%',
                  minHeight: { xs: '70vh', md: '78vh' },
                  border: 0,
                  overflow: 'auto',
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
}
